/* Auto-update — forces installed PWA / browser sessions onto the
   latest deploy without making the user click anything.

   Why we need this: PWAs are sticky. A user installs StudyVET on
   their phone, comes back two weeks later, the launcher serves the
   home-screen icon, the old service worker boots up first, and they
   see whatever app version was current the day they installed.
   Bumping `app/sw.js` CACHE alone is *necessary* (otherwise the new
   bundle never gets fetched) but not *sufficient* — the page is
   already loaded, controlled by the old SW, and won't re-download
   the new bundle until the next hard reload.

   Pipeline (post this rewrite):
     1. App boot calls startAutoUpdate().
     2. We register polling: registration.update() runs every 5 min,
        plus on every visibility-return, plus on every hashchange.
        These are conditional GETs of /sw.js — Hosting answers with
        304 if nothing changed, so the cost is one cheap round-trip.
     3. When a new SW finds a fresh sw.js, it installs in the
        background. The SW's install handler already calls
        self.skipWaiting() (see app/sw.js), so it activates and
        claims clients immediately — no "waiting" state to babysit.
     4. The browser fires `controllerchange` on every active client
        the moment the new SW takes over. We catch that, set a
        sessionStorage breadcrumb, show a 1.5s "Updating…" toast,
        and force window.location.reload(). Page comes back on the
        new code in ~2-3s total.
     5. After the reload, the breadcrumb fires a "you're on the
        latest version" toast so the user knows what happened.

   Side-effects we accept:
   - In-flight unsaved input (untouched form fields, half-typed
     notes) is lost across the reload. Quiz/exam/flashcard sessions
     are persisted to Firestore + localStorage on every grade so
     they resume cleanly. The 1.5s toast is the only warning.
   - Page-state libraries (e.g. router scroll position) reset.
     Acceptable for a 2-week-stale install.

   First-install case (no `navigator.serviceWorker.controller` at
   page load) is excluded — that controllerchange is the SW
   *initially* taking control on the very first visit, not an
   update. Reloading there would put first-time visitors into a
   pointless reload loop.
*/

const POST_RELOAD_FLAG = "sv:justUpdated";
const POLL_INTERVAL_MS = 5 * 60 * 1000;     // 5 min — was 30
const PRE_RELOAD_DELAY_MS = 1500;           // toast read time
let _reloading = false;                     // de-dup guard

export function startAutoUpdate() {
  if (!("serviceWorker" in navigator)) return;

  // Show the post-reload confirmation if we just came back from an
  // auto-update. sessionStorage is the right scope — it survives the
  // reload but doesn't persist across full app exits.
  try {
    if (sessionStorage.getItem(POST_RELOAD_FLAG) === "1") {
      sessionStorage.removeItem(POST_RELOAD_FLAG);
      // Defer the toast until window.toast wires up (toast.js
      // installs window.toast on DOMContentLoaded). 600ms is enough
      // headroom on a cold boot.
      setTimeout(() => {
        try { window.toast?.("Updated to latest version", "ok"); }
        catch { /* never break boot on toast failures */ }
      }, 600);
    }
  } catch { /* private mode etc. */ }

  // Whether the page was already SW-controlled at load time. If not,
  // the very first controllerchange after boot is the initial install
  // taking control — NOT an update — so we ignore it.
  const hadController = !!navigator.serviceWorker.controller;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!hadController) return;
    forceReload();
  });

  // Polling — every 5 min, plus on visibility-return, plus on every
  // route change. Conditional GET of sw.js so cost is negligible.
  navigator.serviceWorker.ready.then((reg) => {
    const safeUpdate = () => reg.update().catch(() => {});

    setInterval(safeUpdate, POLL_INTERVAL_MS);

    document.addEventListener("visibilitychange", () => {
      if (document.visibilityState === "visible") safeUpdate();
    });

    // Hash router fires hashchange on every navigation. Calling
    // update() here means a user clicking through the app on the
    // first day after a deploy lands on the new SW within their
    // first 2-3 page-views, even if they never leave focus.
    window.addEventListener("hashchange", safeUpdate);

    // If a new SW is already sitting in `waiting` at boot (because
    // it installed during a previous tab session and the page
    // wasn't reloaded), wake it up immediately. The SW's own
    // install handler already calls skipWaiting(), so this is
    // belt-and-braces for the rare case where install fired
    // before our message bus was ready.
    if (reg.waiting) {
      try { reg.waiting.postMessage({ type: "SKIP_WAITING" }); } catch {}
    }
    reg.addEventListener("updatefound", () => {
      const sw = reg.installing;
      if (!sw) return;
      sw.addEventListener("statechange", () => {
        if (sw.state === "installed" && navigator.serviceWorker.controller) {
          // New SW installed alongside an active controller →
          // there's an update waiting. Nudge it to skip waiting.
          try { sw.postMessage({ type: "SKIP_WAITING" }); } catch {}
        }
      });
    });
  });
}

function forceReload() {
  if (_reloading) return;
  _reloading = true;

  try { sessionStorage.setItem(POST_RELOAD_FLAG, "1"); }
  catch { /* private mode — toast just won't appear */ }

  // Quick informative toast so the user understands the reload
  // isn't a crash. Uses the global toast helper if present, falls
  // back to a minimal inline pill if it isn't (e.g. update fires
  // before toast.js wired up — unlikely but defensive).
  try {
    if (typeof window.toast === "function") {
      window.toast("Updating to latest version…", "info");
    } else {
      showInlineNotice();
    }
  } catch { /* swallow */ }

  setTimeout(() => {
    try { window.location.reload(); }
    catch { /* reload should never throw, but if it does, give up quietly */ }
  }, PRE_RELOAD_DELAY_MS);
}

// Minimal fallback notice — only used if window.toast hasn't loaded
// yet (toast.js installs it on DOMContentLoaded so this is rare).
function showInlineNotice() {
  if (document.querySelector(".sv-update-notice")) return;
  const el = document.createElement("div");
  el.className = "sv-update-notice";
  el.textContent = "Updating to latest version…";
  el.style.cssText = `
    position: fixed; left: 50%; bottom: 24px; transform: translateX(-50%);
    background: rgba(15, 23, 42, 0.92); color: #fff;
    padding: 10px 18px; border-radius: 999px;
    font: 500 13px/1.4 system-ui, -apple-system, sans-serif;
    box-shadow: 0 8px 24px rgba(15, 23, 42, .25);
    z-index: 9999; pointer-events: none;
  `;
  document.body.appendChild(el);
}
